import React, { useState } from "react";
import { toast } from "sonner";
import { whatsappUrl } from "@/lib/files";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Check, Copy, Link2, Loader2, Mail, MessageCircle, ShieldCheck, Trash2 } from "lucide-react";

const EXPIRY_OPTIONS = [1, 3, 7, 14, 30];
export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export function ExpiryPicker({ days, onChange, expiresAt }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Label className="text-xs">Link valid for</Label>
      <div className="flex gap-1" data-testid="share-expiry">
        {EXPIRY_OPTIONS.map((d) => (
          <button key={d} type="button" onClick={() => onChange(d)} className={`px-2.5 py-1 rounded-full text-xs border ${days === d ? "bg-slate-950 text-white border-slate-950" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`} data-testid={`share-expiry-${d}`}>{d}d</button>
        ))}
      </div>
      {expiresAt && <span className="text-xs text-slate-500 inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> expires {fmtDate(expiresAt)}</span>}
    </div>
  );
}

export function ShareLinkBox({ url, busy }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (!url) return;
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Copy failed — long-press the link to copy it"); }
  };
  return (
    <div className="rounded-md border border-slate-200 p-3">
      <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 flex items-center gap-1"><Link2 className="h-3.5 w-3.5" /> Secure link</div>
      <div className="flex gap-2">
        <Input readOnly value={busy && !url ? "Generating…" : url || ""} className="bg-slate-50 text-xs" data-testid="share-link-input" />
        <Button type="button" variant="outline" onClick={copy} disabled={!url} data-testid="share-copy-button">{copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}</Button>
      </div>
    </div>
  );
}

export function EmailPanel({ busy, result, onSend }) {
  const [emails, setEmails] = useState("");
  const [message, setMessage] = useState("");
  return (
    <div className="rounded-md border border-slate-200 p-3 space-y-2" data-testid="share-email-panel">
      <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> Email</div>
      <Input placeholder="officer@bank.com, valuer@example.com" value={emails} onChange={(e) => setEmails(e.target.value)} data-testid="share-email-input" />
      <Textarea rows={2} placeholder="Optional message" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="share-email-message" />
      <Button type="button" onClick={() => onSend(emails, message)} disabled={busy} className="w-full bg-slate-950 hover:bg-slate-800" data-testid="share-email-send">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 mr-1" /> Send email</>}
      </Button>
      {result && <div className="text-xs text-emerald-700" data-testid="share-email-result">Sent to {result.sent.join(", ")}{result.failed.length ? ` · failed: ${result.failed.join(", ")}` : ""}</div>}
    </div>
  );
}

export function WhatsAppPanel({ text }) {
  const [phone, setPhone] = useState("");
  return (
    <div className="rounded-md border border-emerald-200 bg-emerald-50/50 p-3 space-y-2" data-testid="share-whatsapp-panel">
      <div className="text-[11px] font-bold uppercase tracking-widest text-emerald-800 flex items-center gap-1"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</div>
      <Input type="tel" inputMode="numeric" placeholder="Mobile number (optional, e.g. 98290 12345)" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="share-whatsapp-phone" />
      <p className="text-[11px] text-emerald-900/70">Opens WhatsApp with the lead summary and the secure link pre-filled. Leave the number blank to pick a contact.</p>
      <Button type="button" onClick={() => window.open(whatsappUrl(phone, text), "_blank", "noopener")} disabled={!text} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="share-whatsapp-button">
        <MessageCircle className="h-4 w-4 mr-1" /> Open WhatsApp
      </Button>
    </div>
  );
}

export function ActiveLinks({ links, onRevoke }) {
  const live = links.filter((l) => !l.revoked && !l.expired);
  if (!live.length) return null;
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="share-active-links">
      <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Active links · {live.length}</div>
      <ul className="space-y-1">
        {live.map((l) => (
          <li key={l.id} className="flex items-center justify-between gap-2 text-xs text-slate-600" data-testid={`share-link-${l.id}`}>
            <span>Created {fmtDate(l.created_at)} · expires {fmtDate(l.expires_at)} · opened {l.access_count}×</span>
            <button type="button" onClick={() => onRevoke(l.id)} className="inline-flex items-center gap-1 text-red-600 hover:underline" data-testid={`share-revoke-${l.id}`}><Trash2 className="h-3 w-3" /> Revoke</button>
          </li>
        ))}
      </ul>
    </div>
  );
}
